import { ImageMimeType } from '@types-lib';
import { ImageError, MIME_TO_EXTENSION, AI_DERM_FILE_EXTENSION_TO_TYPE } from './image.utils.model';

export const getAiDermTypeFromFilename = (filename?: string): string => {
  if (!filename) return 'jpeg';
  const fileExtension = filename.split('.').pop()?.toLowerCase() || 'jpg';
  return AI_DERM_FILE_EXTENSION_TO_TYPE[fileExtension] || 'jpeg';
};

export const addImageExtensionFromMimeType = (fileName: string, mimeType: ImageMimeType): string => {
  const extension = MIME_TO_EXTENSION[mimeType] || '';
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');

  return nameWithoutExt + extension;
};

export const blobToFile = (blob: Blob, fileName: string): File => {
  const finalFileName = addImageExtensionFromMimeType(fileName, blob.type as ImageMimeType);

  return new File([blob], finalFileName, { type: blob.type });
};

export const resizeImageAndConvertItToMimeType = async (
  file: File,
  fileName: string = file.name,
  overrideWidth?: number,
  mimeType?: ImageMimeType,
  maxFileSize?: number,
): Promise<File> => {
  try {
    const img = await loadImageFromFile(file);

    const { width, height } = calculateResizeDimensions(img.width, img.height, overrideWidth);

    const { canvas } = drawImageOnCanvas(img, width, height);

    return convertCanvasToFile(canvas, fileName, mimeType || file.type, maxFileSize);
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      img.onload = () => resolve(img);
      img.onerror = () => reject({ status: 100002, message: 'Failed to load image' } as ImageError);
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject({ status: 100003, message: 'Failed to read file' } as ImageError);
    reader.readAsDataURL(file);
  });
};

const calculateResizeDimensions = (
  originalWidth: number,
  originalHeight: number,
  overrideWidth?: number,
): { width: number; height: number } => {
  if (!overrideWidth) {
    return { width: originalWidth, height: originalHeight };
  }

  const ratio = originalWidth / originalHeight;
  return {
    width: overrideWidth,
    height: Math.round(overrideWidth / ratio),
  };
};

const drawImageOnCanvas = (
  img: HTMLImageElement,
  width: number,
  height: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; imageData: ImageData } => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw { status: 100004, message: 'Could not get canvas context' } as ImageError;
  }

  ctx.drawImage(img, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);

  return { canvas, ctx, imageData };
};

const convertCanvasToFile = (
  canvas: HTMLCanvasElement,
  fileName: string,
  mimeType: string,
  maxFileSize?: number,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject({ status: 100005, message: 'Could not convert to blob' });
        return;
      }

      const file = blobToFile(blob, fileName);

      if (maxFileSize && file.size > maxFileSize) {
        reject({ status: 100006, message: 'Image, after conversion, might still be too large' } as ImageError);
      }

      resolve(file);
    }, mimeType);
  });
};

export const isMonochromatic = async (file: File, tolerance = 10): Promise<boolean> => {
  const img = await loadImageFromFile(file);

  const { imageData } = drawImageOnCanvas(img, img.width, img.height);

  const data = imageData.data;
  if (data.length === 0) return false;

  const refR = data[0];
  const refG = data[1];
  const refB = data[2];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    const diff = Math.sqrt(Math.pow(r - refR, 2) + Math.pow(g - refG, 2) + Math.pow(b - refB, 2));

    if (diff > tolerance) {
      return false;
    }
  }

  return true;
};
